import { useEffect } from 'react';
import { useFieldTranslation } from 'src/components/fields/useFieldTranslation';
import {
  useCreateSitePersonalMutation,
  useLazyGetSiteStudyPersonnelDataQuery,
  useUpdateSiteInfoMutation,
  useUpdateSitePersonalFicsMutation,
  useUpdateSitePersonalMutation,
  useUpdateStudyIdentifiedMutation
} from 'src/services/SiteContactInfo';
import { useLazyGetTsdaSiteStudyPersonnelDataQuery } from 'src/services/tsdaIntegrationApis';
import { useAppDispatch } from 'src/store/hooks';
import { setMessage } from 'src/store/Message';
import { updateUserAccess } from 'src/util/util';

export const useSiteContactInfo = () => {
  const { sciDataUpdatedSuccessMsg } = useFieldTranslation();
  const dispatch = useAppDispatch();
  const [
    getSiteStudyPersonnelQueryData,
    {
      data: siteStudyPersonnelQueryData,
      error: siteStudyPersonnelQueryDataError,
      isError: siteStudyPersonnelQueryDataIsError,
      isLoading: siteStudyPersonnelQueryDataLoading,
      isSuccess: siteStudyPersonnelQueryDataIsSuccess
    }
  ] = useLazyGetSiteStudyPersonnelDataQuery();

  const [
    getTSDASiteStudyPersonnelQueryData,
    {
      data: siteTsdaStudyPersonnelQueryData,
      error: siteTsdaStudyPersonnelQueryDataError,
      isError: siteTsdaStudyPersonnelQueryDataIsError,
      isLoading: siteTsdaStudyPersonnelQueryDataLoading,
      isSuccess: siteTsdaStudyPersonnelQueryDataIsSuccess
    }
  ] = useLazyGetTsdaSiteStudyPersonnelDataQuery();

  const [
    createSitePersonal,
    {
      isSuccess: createSitePersonalSuccess,
      data: createSitePersonalData,
      isError: createSitePersonalIsError,
      error: createSitePersonalError
    }
  ] = useCreateSitePersonalMutation();
  const [
    updateSitePersonal,
    {
      isSuccess: updateSitePersonalSuccess,
      data: updateSitePersonalData,
      isError: updateSitePersonalIsError,
      error: updateSitePersonalError
    }
  ] = useUpdateSitePersonalMutation();

  const [
    updateSitePersonalFics,
    {
      isLoading: updateSitePersonalFicsLoading,
      isSuccess: updateSitePersonalFicsSuccess,
      data: updateSitePersonalFicsData,
      isError: updateSitePersonalFicsIsError,
      error: updateSitePersonalFicsError
    }
  ] = useUpdateSitePersonalFicsMutation();

  const [
    updateSiteInfo,
    { isSuccess: updateSiteInfoSuccess, isError: updateSiteInfoIsError, error: updateSiteInfoError }
  ] = useUpdateSiteInfoMutation();

  useEffect(() => {
    if (updateSiteInfoError) {
      updateUserAccess(updateSiteInfoError, dispatch);
    }
  }, [updateSiteInfoError]);
  useEffect(() => {
    if (updateSitePersonalFicsSuccess) {
      dispatch(setMessage({ msgDetails: sciDataUpdatedSuccessMsg, severity: 'success' }));
    }
  }, [updateSitePersonalFicsSuccess]);

  const [
    updateStudyIdentified,
    {
      isSuccess: updateStudyIdentifiedSuccess,
      isError: updateStudyIdentifiedIsError,
      error: updateStudyIdentifiedError
    }
  ] = useUpdateStudyIdentifiedMutation();

  useEffect(() => {
    if (updateStudyIdentifiedError) {
      updateUserAccess(updateStudyIdentifiedError, dispatch);
    }
  }, [updateStudyIdentifiedError]);

  useEffect(() => {
    if (updateSiteInfoSuccess || updateStudyIdentifiedSuccess) {
      dispatch(setMessage({ msgDetails: 'Data updated', severity: 'success' }));
    }
  }, [updateSiteInfoSuccess, updateStudyIdentifiedSuccess]);
  useEffect(() => {
    if (
      updateSitePersonalIsError ||
      updateSitePersonalFicsIsError ||
      updateSiteInfoIsError ||
      updateStudyIdentifiedIsError
    ) {
      const errorData: any =
        updateSitePersonalError || updateSitePersonalFicsError || updateSiteInfoError || updateStudyIdentifiedError;
      if (errorData?.status === 403) {
        dispatch(setMessage({ msgDetails: `${errorData?.status} : Forbbiden Access`, severity: 'error' }));
      } else if (errorData?.status === 401) {
        dispatch(setMessage({ msgDetails: `${errorData?.data?.detail} , Refresh the screen`, severity: 'error' }));
      } else if (errorData?.status === 500) {
        dispatch(setMessage({ msgDetails: errorData?.data?.context?.message, severity: 'error' }));
      } else {
        dispatch(setMessage({ msgDetails: `${errorData?.status} : Internal Server Error`, severity: 'error' }));
      }
    }
  }, [
    updateSitePersonalIsError,
    updateSitePersonalFicsIsError,
    updateSiteInfoIsError,
    updateStudyIdentifiedIsError,
    updateSitePersonalError,
    updateSitePersonalFicsError,
    updateSiteInfoError,
    updateStudyIdentifiedError
  ]);

  return {
    siteStudyPersonnelQueryData,
    siteStudyPersonnelQueryDataIsSuccess,
    updateSiteInfoSuccess,
    updateStudyIdentifiedSuccess,
    updateSitePersonalSuccess,
    createSitePersonalSuccess,
    createSitePersonalIsError,
    updateSitePersonalIsError,
    updateSitePersonalFicsSuccess,
    siteStudyPersonnelQueryDataError,
    siteStudyPersonnelQueryDataIsError,
    siteStudyPersonnelQueryDataLoading,
    getSiteStudyPersonnelQueryData,
    createSitePersonal,
    createSitePersonalData,
    createSitePersonalError,
    updateSitePersonal,
    updateSitePersonalData,
    updateSitePersonalError,
    updateSitePersonalFics,
    updateSitePersonalFicsLoading,
    updateSitePersonalFicsData,
    updateSitePersonalFicsIsError,
    updateSitePersonalFicsError,
    updateSiteInfo,
    updateSiteInfoError,
    updateStudyIdentified,
    getTSDASiteStudyPersonnelQueryData,
    siteTsdaStudyPersonnelQueryData,
    siteTsdaStudyPersonnelQueryDataError,
    siteTsdaStudyPersonnelQueryDataIsError,
    siteTsdaStudyPersonnelQueryDataLoading,
    siteTsdaStudyPersonnelQueryDataIsSuccess
  };
};
